<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import { redirectToStartPage } from '@/router';
import { useEmployeeStore } from '@/stores/employee';
import type { PasswordScore } from '@/types/passwordScore';
import { processErrorWithFeedback } from '@/utils/error';
import toast from '@/utils/toast';
import { confirmPassword, password, required } from '@/utils/validation';
import LoginBackground from '@/views/auth/LoginBackground.vue';
import { debounce, zxcvbnAsync } from '@zxcvbn-ts/core';
import { ref, useTemplateRef, watch } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const employeeStore = useEmployeeStore();

const token = ref<string>(route.params.token as string);
const loading = ref(false);
const passwordInput = ref('');
const confirmPasswordInput = ref('');
const form = useTemplateRef('form');
const debounceUseZxcvbn = debounce(calculatePasswordStrength, 200);
const score = ref<PasswordScore>({ value: 0, color: 'red' });

async function calculatePasswordStrength() {
    if (!passwordInput.value) {
        score.value = { color: 'red-accent-4', value: 0 };
        return;
    }

    const result = await zxcvbnAsync(passwordInput.value);
    switch (result.score) {
        case 4:
            score.value = { color: 'green', value: 100 };
            break;
        case 3:
            score.value = { color: 'yellow', value: 75 };
            break;
        case 2:
            score.value = { color: 'orange', value: 50 };
            break;
        case 1:
            score.value = { color: 'red-darken-1', value: 25 };
            break;
        default:
            score.value = { color: 'red-accent-4', value: 0 };
            break;
    }
}

async function handleResetPassword(): Promise<void> {
    const validationResult = await form.value?.validate();
    if (!validationResult?.valid) return;

    try {
        loading.value = true;
        await employeeStore.resetPassword(token.value, passwordInput.value, confirmPasswordInput.value);

        form.value?.reset();

        toast.success('PasswordSet');
        redirectToStartPage();
    } catch (error) {
        processErrorWithFeedback('Inloggen niet gelukt', error);
    } finally {
        loading.value = false;
    }
}

watch(passwordInput, async () => {
    debounceUseZxcvbn();
});
</script>

<template>
    <LoginBackground>
        <h3>Wachtwoord wijzigen</h3>
        <v-form ref="form" class="w-100" lazy-validation>
            <div class="strength-container">
                <v-progress-linear height="25" :color="score.color" v-model="score.value">
                    <template v-slot:default>Wachtwoord sterkte</template>
                </v-progress-linear>
            </div>

            <v-text-field type="password" name="password" label="Nieuw wachtwoord" v-model="passwordInput" required :rules="[required, password]"/>
            <v-text-field
                class="mt-5"
                type="password"
                name="confirmPassword"
                label="Vul hier nogmaals je wachtwoord in"
                v-model="confirmPasswordInput"
                required
                :rules="[required, confirmPassword(passwordInput)]"
            />
            <div class="d-flex align-center justify-center mt-10">
                <CustomButton class="px-10" :loading="loading" @click="handleResetPassword" color="main-dark" text="Wachtwoord wijzigen" />
            </div>
        </v-form>
    </LoginBackground>
</template>
